// react
import { memo, useMemo } from 'react';

import styled, { css } from 'styled-components';

// models
import { FieldType, validationStringToMethod } from '@karehero/models';

// components
import { Boolean } from '@/components/atoms/Boolean/Boolean';
import { Input } from '@/components/atoms/Input/Input';
import { TextArea } from '@/components/atoms/TextArea/TextArea';
import { VerificationCodeInput } from '@/components/atoms/VerificationCodeInput/VerificationCodeInput';
import { Select } from '@/components/atoms/Select/Select';
import { Date as DatePicker } from '@/components/atoms/Date/Date';
import { MultiSelect } from '@/components/atoms/MultiSelect/MultiSelect';
import { MultiSelectChip } from '@/components/atoms/MultiSelectChip/MultiSelectChip';
import { MultiSelectBox } from '@/components/atoms/MultiSelectBox/MultiSelectBox';
import { Slider } from '@/components/atoms/Slider/Slider';
import {
  RadioGroup,
  RadioGroupVariant,
} from '@/components/atoms/RadioGroup/RadioGroup';
import { BooleanTable } from '@/components/atoms/BooleanTable/BooleanTable';
import { TimePicker } from '@/components/atoms/TimePicker/TimePicker';
import { CheckboxVariant } from '@/components/atoms/Checkbox/Checkbox';
import { ToggleSelect } from '@/components/atoms/ToggleSelect/ToggleSelect';

import { ContactField } from '@/components/molecules/ContactField/ContactField';

import { AddressField } from '@/components/molecules/AddressField/AddressField';

import { Switch } from '@/components/atoms/Switch/Switch';

import { CareCircleMemberSelector } from '@/components/molecules/CareCircleMemberSelector/CareCircleMemberSelector';

import { FieldDef } from '@/components/organisms/EditorFields/fieldHelper';
import { validationAsString } from '@/components/organisms/Editor/Editor';
import { QuestionTable } from '@/components/organisms/QuestionTable/QuestionTable';

// hooks
import { useValidator } from '@/hooks';
import { Icons } from '@/components/atoms/Icon/icons';

interface EditorFieldsProps<T = any> {
  fields: { [key in keyof T]: FieldDef };
  value: T;
  onChange: (value: T, key: string) => void;
  isDisabled?: boolean;
  isRow?: boolean;
  isFullWidth?: boolean;
  setIsValid?: (isValid: boolean) => void;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  actions?: React.ReactNode;
}

/**
 * EditorFields is the fields of the editor.
 */
export const EditorFields = memo(
  ({
    fields,
    value,
    onChange,
    isDisabled = false,
    isRow = false,
    isFullWidth = true,
    setIsValid,
    onSubmit,
    actions,
  }: EditorFieldsProps) => {
    // hooks
    const { createSetIsValid } = useValidator(setIsValid);

    // convert fields into an array ordered by the order property
    const orderedFields = useMemo(() => {
      let orderedFields: {
        fieldValue: FieldDef;
        fieldKey: string;
      }[] = [];
      Object.keys(fields).forEach((fieldKey, i) => {
        const fieldValue = fields[fieldKey];
        const index = fieldValue?.order ? fieldValue.order : 99999 + i;
        orderedFields[index] = {
          fieldValue: fieldValue,
          fieldKey: fieldKey,
        };
      });
      return orderedFields;
    }, [fields]);

    const orderedFieldsComputed = useMemo(
      () =>
        orderedFields.map((orderedField) => {
          const fieldValue = orderedField.fieldValue;
          const fieldKey = orderedField.fieldKey;

          if (!fieldValue) return null;

          let validation = [fieldValue?.validation];
          if (fieldValue?.validationRules) {
            validation.push(
              validationStringToMethod(
                validationAsString(fieldValue?.validationRules),
              ),
            );
          }

          const validationMethod = (
            value?: string | number | boolean,
          ): boolean => {
            let isValid = validation.every((v) =>
              v === undefined ? true : v?.(value),
            );
            if (isValid === undefined) isValid = true;
            if (
              fieldValue?.isRequired &&
              (value === undefined || value === null || value === '')
            ) {
              isValid = false;
            }
            createSetIsValid(fieldKey)(isValid);
            return isValid;
          };

          const genericProps = {
            id: fieldKey,
            ariaLabel: fieldValue?.ariaLabel || fieldValue?.label,
            placeholder: fieldValue?.placeholder,
            label: fieldValue?.label,
            description: fieldValue?.description,
            isHideTitle: fieldValue?.isHideTitle,
            isDisabled: fieldValue?.isDisabled || isDisabled,
            tooltip: fieldValue?.tooltip,
            isRequired: fieldValue?.isRequired,
            value: value?.[fieldKey] !== undefined ? value?.[fieldKey] : '',
            validate: validationMethod,
            autoFocus: fieldValue?.field.autoFocus,
            showError: fieldValue?.showError,
            onChange: (
              newValue: string | number | boolean | symbol | undefined | any,
            ) => {
              onChange({ ...value, [fieldKey]: newValue }, fieldKey);
            },
          };

          switch (fieldValue.field.type) {
            case FieldType.Text:
            case FieldType.Time:
              return (
                <Input
                  {...genericProps}
                  iconLeft={fieldValue?.iconLeft as Icons | undefined}
                  key={fieldKey}
                />
              );
            case FieldType.TextArea:
              return <TextArea {...genericProps} key={fieldKey} />;
            case FieldType.Number:
              return <Input {...genericProps} type='number' key={fieldKey} />;
            case FieldType.Email:
              return <Input {...genericProps} type='email' key={fieldKey} />;
            case FieldType.Password:
              return <Input {...genericProps} type='password' key={fieldKey} />;
            case FieldType.VerificationCode:
              return <VerificationCodeInput {...genericProps} key={fieldKey} />;
            case FieldType.Select:
            case FieldType.SelectCustom:
              return (
                <Select
                  {...genericProps}
                  options={fieldValue?.field.options || []}
                  key={fieldKey}
                />
              );
            case FieldType.MultiSelect:
            case FieldType.MultiSelectCustom:
              return (
                <MultiSelect
                  {...genericProps}
                  isCustom={fieldValue.field.isCustom || false}
                  options={fieldValue.field.options || []}
                  key={fieldKey}
                />
              );
            case FieldType.MultiSelectChip:
              return (
                <MultiSelectChip
                  {...genericProps}
                  isCustom={fieldValue.field.isCustom || false}
                  options={fieldValue.field.options || []}
                  key={fieldKey}
                />
              );
            case FieldType.MultiSelectBox:
              const { ...filteredProps } = genericProps;
              return (
                <MultiSelectBox
                  {...filteredProps}
                  variant={fieldValue.field.variant as CheckboxVariant}
                  options={fieldValue.field.options || []}
                  maxColumns={fieldValue.field.maxColumns || 1}
                  isCustom={fieldValue.field.isCustom || false}
                  size={fieldValue.field.size || 'sm'}
                  key={fieldKey}
                />
              );
            case FieldType.Consent:
              const { ...filteredConsentProps } = genericProps;
              return (
                <MultiSelectBox
                  {...filteredConsentProps}
                  variant={
                    (fieldValue.field.variant || 'default') as CheckboxVariant
                  }
                  options={fieldValue.field.options || []}
                  maxColumns={fieldValue.field.maxColumns || 1}
                  isCustom={fieldValue.field.isCustom || false}
                  size={fieldValue.field.size || 'sm'}
                  key={fieldKey}
                />
              );
            case FieldType.RadioGroup:
              return (
                <RadioGroup
                  {...genericProps}
                  maxColumns={fieldValue.field.maxColumns || 2}
                  variant={fieldValue.field.variant as RadioGroupVariant}
                  options={fieldValue.field.options || []}
                  key={fieldKey}
                />
              );
            case FieldType.BooleanTable:
              return (
                <BooleanTable
                  {...genericProps}
                  title={fieldValue.field.table?.title}
                  columns={fieldValue.field.table?.columns || []}
                  rows={fieldValue.field.table?.rows || []}
                  key={fieldKey}
                />
              );
            case FieldType.QuestionTable:
              return (
                <QuestionTable
                  {...genericProps}
                  value={value}
                  onChange={genericProps.onChange}
                  fieldValue={orderedField.fieldValue}
                  fieldKey={fieldKey}
                  key={fieldKey}
                />
              );
            case FieldType.Boolean:
              return <Boolean {...genericProps} key={fieldKey} />;
            case FieldType.Switch:
              return <Switch {...genericProps} size='sm' key={fieldKey} />;
            case FieldType.DatePicker:
            case FieldType.DateRange:
              return <DatePicker {...genericProps} key={fieldKey} />;
            case FieldType.Contact:
              return <ContactField {...genericProps} key={fieldKey} />;
            case FieldType.Address:
              return <AddressField {...genericProps} key={fieldKey} />;
            case FieldType.Slider:
              return (
                <Slider
                  {...genericProps}
                  min={fieldValue?.field?.limits?.min || 0}
                  max={fieldValue?.field?.limits?.max || 10}
                  lowLabel={fieldValue?.field?.labels?.low}
                  highLabel={fieldValue?.field?.labels?.high}
                  key={fieldKey}
                />
              );
            case FieldType.SliderSelect:
              return (
                <Slider
                  {...genericProps}
                  isSegmented
                  options={fieldValue.field.options || []}
                  lowLabel={fieldValue?.field?.labels?.low}
                  highLabel={fieldValue?.field?.labels?.high}
                  key={fieldKey}
                />
              );
            case FieldType.SliderSeverity:
              return (
                <Slider
                  {...genericProps}
                  min={1}
                  max={5}
                  isSegmented
                  segmentLabels={[
                    'None',
                    'Mild',
                    'Moderate',
                    'Significant',
                    'Critical',
                  ]}
                  lowLabel={fieldValue?.field?.labels?.low}
                  highLabel={fieldValue?.field?.labels?.high}
                  key={fieldKey}
                />
              );
            case FieldType.CareCircleMemberSelector:
              return (
                <CareCircleMemberSelector
                  {...genericProps}
                  careCircle={value}
                  relationshipOptions={fieldValue.field.options || []}
                  onInvite={fieldValue.onInvite}
                  key={fieldKey}
                />
              );
            case FieldType.TimePicker:
              return (
                <TimePicker
                  {...genericProps}
                  options={fieldValue.field.options || []}
                  key={fieldKey}
                />
              );
            case FieldType.ToggleSelect:
              return (
                <ToggleSelect
                  {...genericProps}
                  options={fieldValue.field.options || []}
                  key={fieldKey}
                />
              );

            default:
              return null;
          }
        }),
      [orderedFields, fields, value, onChange, isDisabled],
    );

    return (
      <StyledEditorFields
        $isRow={isRow}
        $isFullWidth={isFullWidth}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.(e);
        }}
      >
        {orderedFieldsComputed}
        {actions}
      </StyledEditorFields>
    );
  },
);

interface StyledEditorFieldsProps {
  $isRow: boolean;
  $isFullWidth?: boolean;
}

const StyledEditorFields = styled.form<StyledEditorFieldsProps>`
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.$isRow &&
    css`
      flex-direction: row;
    `}
  gap: ${(props) => props.theme.spacing[32]};
  ${(props) =>
    props.$isFullWidth &&
    css`
      width: 100%;
    `};
`;

EditorFields.displayName = 'EditorFields';

export default EditorFields;
